import {
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { ChatBotSettingsFormValues } from "@jugl-web/domain-resources/chat-bot";
import { MAX_CTA_LENGTH } from "@jugl-web/domain-resources/chat-bot/consts";
import { getDefaultCTAVisual } from "@jugl-web/domain-resources/chat-bot/utils/defaultCTAVisuals/defaultCTAVisuals";
import { Button, TextInput } from "@jugl-web/ui-components";
import { Switch } from "@jugl-web/ui-components/cross-platform/Switch";
import { cx, useTranslations } from "@jugl-web/utils";
import { FC, useMemo, useState } from "react";
import { Controller, useFieldArray, useFormContext } from "react-hook-form";
import { SelectCTAVisualDialog } from "../SelectCTAVisualDialog";
import { ReactComponent as ReorderIcon } from "./assets/reorder.svg";
import { ReactComponent as SelectIcon } from "./assets/select.svg";

export const AppearanceCTATab: FC = () => {
  const { t } = useTranslations();

  const [isCTAVisualDialogOpen, setIsCTAVisualDialogOpen] = useState(false);

  const { watch, control, setValue, getValues } =
    useFormContext<ChatBotSettingsFormValues>();

  const ctaVisual = watch("ctaVisual");

  const { fields, move } = useFieldArray<ChatBotSettingsFormValues>({
    control,
    name: "ctas",
  });

  const ctas = watch("ctas") || [];

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = fields.findIndex((item) => item.id === active.id);
      const newIndex = fields.findIndex((item) => item.id === over.id);

      move(oldIndex, newIndex);
    }
  };

  const filledCTAsCount = ctas.filter((cta) => cta.value.trim() !== "").length;
  const isDragDisabled = filledCTAsCount < 2;

  const selectedCtaVisualLabel = useMemo((): string | null => {
    if (!ctaVisual) {
      return null;
    }

    if (ctaVisual instanceof File) {
      return t({
        id: "client-support-page.cta-visual-custom-file",
        defaultMessage: "Custom file",
      });
    }

    if (ctaVisual.startsWith("default")) {
      return getDefaultCTAVisual(ctaVisual).name;
    }

    return t({
      id: "client-support-page.cta-visual-custom-file",
      defaultMessage: "Custom file",
    });
  }, [ctaVisual, t]);

  return (
    <div className="flex flex-col gap-8">
      <div className="flex flex-col gap-1">
        <span className="font-secondary text-lg font-medium text-black">
          {t({
            id: "client-support-page.call-to-action",
            defaultMessage: "Call to Action",
          })}
        </span>

        <span className="text-dark-700 font-secondary text-sm leading-[140%] tracking-[0.14px]">
          {t({
            id: "client-support-page.call-to-action-description",
            defaultMessage:
              "Engage Website visitors to interact with your Web Chat by setting proper Call to Actions. Set 1 or 2 short but clean messages, related to the help that you can provide to the Customer 🙌",
          })}
        </span>
      </div>

      <Separator />

      <div className="flex items-center justify-between gap-2">
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-2.5">
            <Controller
              control={control}
              name="ctaVisualEnabled"
              render={({ field: { value, onChange } }) => (
                <Switch
                  id="cta-visual-enabled"
                  isChecked={value}
                  onChange={(checked) => {
                    if (checked && !getValues("ctaVisual")) {
                      setIsCTAVisualDialogOpen(true);
                      return;
                    }

                    onChange(checked);
                  }}
                />
              )}
            />
            <label
              htmlFor="cta-visual-enabled"
              className="text-dark text-sm font-medium"
            >
              {t({
                id: "client-support-page.include-visuals",
                defaultMessage: "Include Visuals",
              })}
            </label>
          </div>
          {selectedCtaVisualLabel !== null && (
            <span className="text-dark-700 text-sm">
              {selectedCtaVisualLabel}
            </span>
          )}
        </div>
        <Button
          variant="contained"
          color="grey"
          size="small"
          className="bg-grey-100 hover:bg-grey-200 h-8 font-medium"
          iconStart={<SelectIcon />}
          onClick={() => setIsCTAVisualDialogOpen(true)}
        >
          {t({ id: "common.select", defaultMessage: "Select" })}
        </Button>
      </div>

      <SelectCTAVisualDialog
        isOpen={isCTAVisualDialogOpen}
        selectedVisual={ctaVisual}
        onVisualChange={(visual) => {
          setValue("ctaVisual", visual, { shouldDirty: true });
          setValue("ctaVisualEnabled", true, { shouldDirty: true });
        }}
        onClose={() => setIsCTAVisualDialogOpen(false)}
      />

      <Separator />

      <div className="flex flex-col gap-6">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={fields}
            strategy={verticalListSortingStrategy}
          >
            {fields.map((field, index) => (
              <SortableCTAInput
                key={field.id}
                item={field}
                index={index}
                disabled={isDragDisabled}
              />
            ))}
          </SortableContext>
        </DndContext>
      </div>
    </div>
  );
};

interface SortableCTAInputProps {
  item: ChatBotSettingsFormValues["ctas"][number];
  index: number;
  disabled: boolean;
}

const SortableCTAInput: FC<SortableCTAInputProps> = ({
  item,
  index,
  disabled,
}) => {
  const { t } = useTranslations();
  const { control } = useFormContext<ChatBotSettingsFormValues>();

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id, disabled });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} className="flex items-center gap-2">
      <Controller
        control={control}
        name={`ctas.${index}.value`}
        render={({ field: { onChange, ...props } }) => (
          <TextInput
            labelPosition="border-overlap"
            maxLength={MAX_CTA_LENGTH}
            lengthIndicator={{
              current: props.value.length,
              max: MAX_CTA_LENGTH,
            }}
            label={t(
              {
                id: "client-support-page.cta-number",
                defaultMessage: "CTA {number}",
              },
              { number: index + 1 }
            )}
            placeholder={t({ id: "common.enter", defaultMessage: "Enter" })}
            classNames={{
              wrapperClassName: "w-full",
            }}
            onChange={(e) => onChange(e.target.value.trimStart())}
            {...props}
          />
        )}
      />
      <div
        className={cx(
          "leading-[0]",
          disabled
            ? "cursor-not-allowed opacity-40"
            : "cursor-grab touch-none active:cursor-grabbing"
        )}
        {...(!disabled ? attributes : {})}
        {...(!disabled ? listeners : {})}
      >
        <ReorderIcon />
      </div>
    </div>
  );
};

const Separator: FC<{ className?: string }> = ({ className }) => (
  <div className={cx("h-px w-full bg-[#F2F2F2]", className)} />
);
